/* eslint-disable no-alert */
/* eslint-disable no-restricted-globals */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';

import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  InputGroup,
  Spinner,
} from 'reactstrap';

import {
  Table, Thead, Tbody, Tr, Th, Td,
} from 'react-super-responsive-table';

// import moment from 'moment';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import classnames from 'classnames';
import moment from 'moment';
import Papa from 'papaparse';

import { Formik, Form } from 'formik';
import * as Yup from 'yup';

// import { Can } from '../../utilities/Can';
// import classnames from 'classnames';
import BaseGrid from '../../components/Common/BaseGrid';
import {
  get, post, del, getFile,
} from '../../helpers/api_helper';
import OrderModal from '../../components/Sourcing/OrderModal';
import Inspector from '../../components/Item/inspector';

const areaStockCache = {};

function formatSourcingAlert(value) {
  return value ? (
    <span className="text-danger fw-bold">
      <span className="mdi mdi-alert me-1" aria-hidden="true" />
      {value}
    </span>
  ) : '';
}

function formatAreaStock(areas, isLoading) {
  if (isLoading) {
    return <Spinner size="sm" color="secondary" />;
  }

  if (!areas || areas.length === 0) {
    return '0';
  }

  return areas.map((area) => area.qty).reduce((sum, qty) => sum + qty, 0);
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value) || 0);
}

function formatHalfLifeWeeks(weeks, t) {
  if (!Number.isFinite(weeks) || weeks < 0) {
    return t('Not available yet');
  }

  if (weeks < 1) {
    return t('Less than 1 week');
  }

  return `${Number(weeks).toFixed(1)} ${t('weeks')}`;
}

function formatMetricDate(metric, t) {
  if (!metric?.milestone_date) {
    return null;
  }

  const milestoneDate = moment(metric.milestone_date)
    .format('MMMM D, YYYY');

  return metric.mode === 'actual'
    ? `${t('Reached')} ${milestoneDate}`
    : `${t('Expected')} ${milestoneDate}`;
}

function buildWeeklyTrendPoints(points = []) {
  const weeklyBuckets = points.reduce((acc, point) => {
    const weekStart = moment(point.date).startOf('isoWeek');
    const key = weekStart.format('YYYY-MM-DD');

    if (!acc[key]) {
      acc[key] = {
        date: weekStart.format('YYYY-MM-DD'),
        sales_count: 0,
        revenue: 0,
      };
    }

    acc[key].sales_count += Number(point.sales_count) || 0;
    acc[key].revenue += Number(point.revenue) || 0;

    return acc;
  }, {});

  return Object.values(weeklyBuckets)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((point) => ({
      ...point,
      revenue: Number(point.revenue.toFixed(2)),
    }));
}

function buildTrendLine(series = []) {
  const values = Array.isArray(series) ? series.map((value) => Number(value) || 0) : [];
  const n = values.length;
  const sumX = values.reduce((sum, _, index) => sum + index, 0);
  const sumY = values.reduce((sum, value) => sum + value, 0);
  const sumXY = values.reduce((sum, value, index) => sum + (index * value), 0);
  const sumXX = values.reduce((sum, _, index) => sum + (index * index), 0);
  const denominator = (n * sumXX) - (sumX * sumX);
  const slope = denominator !== 0
    ? (((n * sumXY) - (sumX * sumY)) / denominator)
    : 0;
  const intercept = n > 0 ? ((sumY - (slope * sumX)) / n) : 0;

  return values.map((_, index) => Math.max(0, Number((intercept + (slope * index)).toFixed(2))));
}

function buildPopularityDistributionPoints(items, totalQty, bucketCount = 24) {
  const normalizedItems = (Array.isArray(items) ? items : [])
    .filter((item) => (Number(item.inducted_qty) || 0) > 0)
    .map((item) => ({
      popularity: Number(item.popularity) || 0,
      qty: Number(item.inducted_qty) || 0,
    }))
    .sort((a, b) => {
      if (b.popularity !== a.popularity) {
        return b.popularity - a.popularity;
      }

      return b.qty - a.qty;
    });

  const normalizedTotalQty = Number(totalQty) || normalizedItems.reduce(
    (sum, item) => sum + item.qty,
    0,
  );

  if (normalizedItems.length === 0 || normalizedTotalQty <= 0) {
    return [];
  }

  const effectiveBucketCount = Math.max(6, Math.min(bucketCount, normalizedItems.length * 2));
  const bucketSize = normalizedTotalQty / effectiveBucketCount;
  let itemIndex = 0;
  let qtyConsumedFromItem = 0;
  const points = [];

  for (let bucketIndex = 0; bucketIndex < effectiveBucketCount; bucketIndex += 1) {
    let bucketRemaining = bucketSize;
    let weightedPopularity = 0;
    const rangeStartQty = bucketIndex * bucketSize;

    while (bucketRemaining > 0.0001 && itemIndex < normalizedItems.length) {
      const currentItem = normalizedItems[itemIndex];
      const availableQty = currentItem.qty - qtyConsumedFromItem;
      const allocatedQty = Math.min(bucketRemaining, availableQty);

      weightedPopularity += allocatedQty * currentItem.popularity;
      bucketRemaining -= allocatedQty;
      qtyConsumedFromItem += allocatedQty;

      if (qtyConsumedFromItem >= currentItem.qty - 0.0001) {
        itemIndex += 1;
        qtyConsumedFromItem = 0;
      }
    }

    const filledQty = bucketSize - bucketRemaining;
    const averagePopularity = filledQty > 0 ? (weightedPopularity / filledQty) : 0;
    const rangeEndQty = Math.min(normalizedTotalQty, (bucketIndex + 1) * bucketSize);
    const cumulativeShare = (rangeEndQty / normalizedTotalQty) * 100;

    points.push({
      x: Number(cumulativeShare.toFixed(1)),
      y: Number(averagePopularity.toFixed(3)),
      range_start_qty: Number(rangeStartQty.toFixed(1)),
      range_end_qty: Number(rangeEndQty.toFixed(1)),
    });
  }

  if (points.length === 0) {
    return [];
  }

  return [
    {
      ...points[0],
      x: 0,
      range_start_qty: 0,
      range_end_qty: 0,
    },
    ...points,
  ];
}

const summaryChartColors = [
  '#188ae2',
  '#10d69f',
  '#ffb020',
  '#ff4560',
  '#775dd0',
  '#00b8d9',
  '#7a4cff',
  '#26a69a',
  '#ff6f61',
  '#546e7a',
  '#8bc34a',
  '#fb8c00',
];

const siteDistributionColors = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#84cc16',
  '#f97316',
  '#6366f1',
  '#14b8a6',
];

const summaryDataDefaults = {
  shipped: null,
  losers: null,
  financials: null,
  salesTrend: null,
  categories: [],
};

const summaryLoadingDefaults = {
  shipped: false,
  losers: false,
  financials: false,
  salesTrend: false,
  categories: false,
};

const summaryErrorDefaults = {
  shipped: null,
  losers: null,
  financials: null,
  salesTrend: null,
  categories: null,
};

function Order(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const { t } = props;

  const { id } = useParams();

  const qtyRef = useRef(null);
  const summarySearchRef = useRef(null);
  const manifestUploadRef = useRef(null);
  const searchFieldRef = useRef(null);

  // const [editModalOpen, setEditModalOpen] = useState(false);
  const [entity, setEntity] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [, setProgram] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [signal, setSignal] = useState(0);
  const [activeTab, setActiveTab] = useState('0');
  const [subActiveTab, setSubActiveTab] = useState('1');
  const [searchTerm, setSearchTerm] = useState(null);
  const [inductionSearchValue, setInductionSearchValue] = useState('');
  const [inductQty, setInductQty] = useState(1);
  const [part, setPart] = useState(null);
  const [itemAreaStock, setItemAreaStock] = useState({});
  const [loadingAreaStock, setLoadingAreaStock] = useState({});
  const [summaryData, setSummaryData] = useState(summaryDataDefaults);
  const [summaryLoading, setSummaryLoading] = useState(summaryLoadingDefaults);
  const [summaryErrors, setSummaryErrors] = useState(summaryErrorDefaults);
  const [activeCategoryTreeTab, setActiveCategoryTreeTab] = useState('');

  useEffect(() => {
    const loadEntity = async () => {
      try {
        if (entity === null) {
          setIsInitialLoading(true);
        }
        const response = await get(`/api/sourcing/orders/${id}`);
        setEntity(response);
      } catch (error) {
        toastr.error(error.message, 'Error');
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadEntity();
  }, [id, signal]);

  useEffect(() => {
    if (entity === null) {
      return;
    }

    if (entity.supplier !== undefined) {
      const prg = entity.supplier.programs
        .filter((x) => x.name === entity.supplier_program)
        .shift();

      setProgram(prg);
    }
  }, [entity]);

  useEffect(() => {
    if (activeTab === '3' && summarySearchRef.current) {
      summarySearchRef.current.focus();
    }
  }, [activeTab, entity]);

  useEffect(() => {
    if (!Array.isArray(summaryData.categories) || summaryData.categories.length === 0) {
      setActiveCategoryTreeTab('');
      return;
    }

    const hasActiveTree = summaryData.categories.some(
      (tree) => String(tree.tree_id) === activeCategoryTreeTab,
    );

    if (!hasActiveTree) {
      setActiveCategoryTreeTab(String(summaryData.categories[0].tree_id));
    }
  }, [summaryData.categories, activeCategoryTreeTab]);

  useEffect(() => {
    if (!entity?.id || activeTab !== '3') {
      return undefined;
    }

    let isActive = true;
    const orderId = entity.id;
    const endpoints = [
      ['shipped', `/api/sourcing/orders/${orderId}/summary/shipped`, null],
      ['losers', `/api/sourcing/orders/${orderId}/summary/losers`, null],
      ['financials', `/api/sourcing/orders/${orderId}/summary/financials`, null],
      ['salesTrend', `/api/sourcing/orders/${orderId}/summary/sales-trend`, null],
      ['categories', `/api/sourcing/orders/${orderId}/summary/categories`, []],
    ];

    setSummaryData(summaryDataDefaults);
    setSummaryErrors(summaryErrorDefaults);
    setSummaryLoading({
      shipped: true,
      losers: true,
      financials: true,
      salesTrend: true,
      categories: true,
    });

    endpoints.forEach(([key, url, fallbackValue]) => {
      const loadSection = async () => {
        try {
          const response = await get(url);

          if (!isActive) {
            return;
          }

          setSummaryData((prev) => ({
            ...prev,
            [key]: response ?? fallbackValue,
          }));
        } catch (error) {
          if (!isActive) {
            return;
          }

          setSummaryErrors((prev) => ({
            ...prev,
            [key]: error.message,
          }));
        }

        if (!isActive) {
          return;
        }

        setSummaryLoading((prev) => ({
          ...prev,
          [key]: false,
        }));
      };

      loadSection();
    });

    return () => {
      isActive = false;
    };
  }, [entity, activeTab]);

  const loadAreaStockForItems = (results = []) => {
    const itemIds = results
      .map((item) => item.id)
      .filter((itemId) => itemId !== undefined);

    setItemAreaStock((prev) => itemIds.reduce(
      (acc, itemId) => ({
        ...acc,
        [itemId]: areaStockCache[itemId] || prev[itemId],
      }),
      { ...prev },
    ));

    const uncachedItemIds = itemIds.filter(
      (itemId) => areaStockCache[itemId] === undefined,
    );

    if (uncachedItemIds.length === 0) {
      return;
    }

    setLoadingAreaStock((prev) => uncachedItemIds.reduce(
      (acc, itemId) => ({
        ...acc,
        [itemId]: true,
      }),
      { ...prev },
    ));

    Promise.all(
      uncachedItemIds.map(async (itemId) => {
        try {
          const response = await get(
            `/api/burnsville/areaStockByItemId/${itemId}`,
          );
          const areas = Array.isArray(response?.areas)
            ? response.areas.filter((area) => area.id === 5 || area.id === 8
              || area.id === 27 || area.id === 25)
            : [];

          areaStockCache[itemId] = areas;

          return [itemId, areas];
        } catch (error) {
          areaStockCache[itemId] = [];
          return [itemId, []];
        }
      }),
    ).then((loadedEntries) => {
      setItemAreaStock((prev) => loadedEntries.reduce(
        (acc, [itemId, areas]) => ({
          ...acc,
          [itemId]: areas,
        }),
        { ...prev },
      ));

      setLoadingAreaStock((prev) => uncachedItemIds.reduce(
        (acc, itemId) => ({
          ...acc,
          [itemId]: false,
        }),
        { ...prev },
      ));
    });
  };

  const reprintItem = async (item, qty) => {
    try {
      const remoteItem = await get(
        `/api/burnsville/inventoryByItemId/${item.id}`,
      );

      // print label
      await post(
        `/api/burnsville/printDZLabel/${remoteItem.stock_id}?qty=${qty}`,
        {
          lot: entity.lot,
          part_number: item.part_number,
        },
      );

      toastr.success('Label printed', 'Success');

      setSignal(signal + 1);
    } catch (error) {
      toastr.error(error.message, 'Error');
    }
  };

  const induct = async (item) => {
    try {
      const induction = await post(`/api/sourcing/orders/induct/${entity.id}`, {
        item_id: item.id,
        qty: inductQty,
      });
      toastr.success('Item inducted', 'Success');

      setSignal(signal + 1);
      document.getElementById('induction_search').value = '';
      document.getElementById('induction_search').focus();

      const remoteItem = await get(
        `/api/burnsville/inventoryByItemId/${item.id}`,
      );

      // print label
      await post(
        `/api/burnsville/printDZLabel/${remoteItem.stock_id}?qty=${inductQty}`,
        {
          lot: induction.sourcing_order.lot,
          part_number: item.part_number,
        },
      );

      setInductQty(1);
      qtyRef.current.value = 1;

      // Focus the search field and select all
      searchFieldRef.current.focus();
      searchFieldRef.current.select();
    } catch (error) {
      toastr.error(error.message, 'Error');
    }
  };

  const processKit = async (kit) => {
    try {
      const ids = kit.items.map((x) => x.id);
      const params = new URLSearchParams({ items: ids.join(',') });
      if (kit.sku) params.set('sku', kit.sku);
      const remoteKit = await get(
        `/api/burnsville/kit?${params.toString()}`,
      );

      // log part as printed
      // refresh history
      await Promise.all(
        ids.map((tid) => post(`/api/sourcing/orders/induct/${entity.id}`, {
          item_id: tid,
          qty: inductQty,
        })),
      );

      await post(`/api/burnsville/printDZLabel/${remoteKit.stock_id}`, {
        lot: entity.lot,
        part_number: remoteKit.sku || `KIT-${remoteKit.stock_id}`,
        serial: `${remoteKit.stock_id}${Math.random()
          .toString(36)
          .substring(2, 8)
          .toUpperCase()}`,
      });

      document.getElementById('induction_search').value = '';
      document.getElementById('induction_search').focus();

      setSignal(signal + 1);
      setPart(null);
      setInductQty(1);

      toastr.success('Kit processed', 'Success');
    } catch (error) {
      toastr.error(error.message, 'Error');
    }
  };

  const createValidationSchema = (items) => {
    const manifestSchema = items !== undefined
      && items.reduce((acc, item) => {
        acc[`${item.id}`] = Yup.object().shape({
          qty: Yup.number().required('Quantity is required'),
        });
        return acc;
      }, {});

    return Yup.object().shape({
      manifest: Yup.object().shape(manifestSchema),
    });
  };

  const print = async (itemId, qty) => {
    try {
      await post(`/api/sourcing/orders/print/${id}`, {
        manifest: { [itemId]: { qty } },
      });
    } catch (error) {
      toastr.error(error.message, 'Error');
    }
  };

  const removeRecord = async (recordId) => {
    try {
      if (confirm('Are you sure?')) {
        await del(`/api/sourcing/orders/remove/${recordId}`);
        setSignal(signal + 1);
        toastr.success('Record removed', 'Success');
      }
    } catch (error) {
      toastr.error(error.message, 'Error');
    }
  };

  const calculateCost = async () => {
    try {
      await post(`/api/sourcing/orders/${id}/cost`);
      toastr.success('Cost calculated', 'Success');
      setSignal(signal + 1);
    } catch (error) {
      toastr.error(error.message, 'Error');
    }
  };

  const uploadManifest = async (event) => {
    const file = event.target.files[0];
    const input = event.target;

    if (!file || !entity?.id) {
      input.value = '';
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const response = await post(`/api/sourcing/orders/upload-manifest/${entity.id}`, {
            rows: results.data,
          });

          if (response.errors.length > 0) {
            toastr.warning(
              `Processed ${response.inducted_rows} row(s), with ${response.errors.length} error(s).`,
              'Manifest processed with warnings',
            );
          } else {
            toastr.success(
              `Processed ${response.inducted_rows} row(s) and printed ${response.inducted_qty} label(s).`,
              'Manifest processed',
            );
          }

          setSignal(signal + 1);
        } catch (error) {
          toastr.error(error.message, 'Error processing manifest');
        } finally {
          input.value = '';
        }
      },
      error: (error) => {
        input.value = '';
        toastr.error(error.message, 'Error parsing manifest');
      },
    });
  };

  const downloadManifest = async () => {
    try {
      const response = await getFile(`/api/sourcing/orders/${id}/inductions/manifest-csv`);
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const reference = entity?.reference || id;

      link.href = downloadUrl;
      link.setAttribute('download', `sourcing_manifest_${reference}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toastr.error(error.message, 'Error');
    }
  };

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const toggleSub = (tab) => {
    if (subActiveTab !== tab) setSubActiveTab(tab);
  };

  const submitInductionSearch = (value) => {
    const nextValue = value.trim();
    setInductionSearchValue(nextValue);
    setSearchTerm(nextValue);
    setActiveTab('1');
  };

  const inspectSummaryItem = async (itemId) => {
    try {
      const item = await get(`/api/items/get/${itemId}`);
      setPart(item);
    } catch (error) {
      toastr.error(error.message, 'Error');
    }
  };

  const renderSummaryCardLoader = (message) => (
    <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-muted py-4">
      <Spinner color="primary" />
      <div className="mt-3">{message}</div>
    </div>
  );

  const renderSummaryCardError = () => (
    <div className="text-danger py-2">
      {t('Unable to load this summary card right now')}
    </div>
  );

  const getCategoryRoot = (node) => {
    if (!node?.path_segments || node.path_segments.length === 0) {
      return node?.category_name || '';
    }

    return node.path_segments[0];
  };

  const renderCategoryHierarchy = (
    nodes = [],
    colorMap = {},
    parentQty = 0,
    depth = 0,
  ) => nodes.map((node) => (
    <div key={node.node_id || node.path}>
      <div
        className="d-flex align-items-start justify-content-between gap-3 py-2 border-bottom sourcing-order-page__category-key-row"
        style={{ paddingLeft: `${depth * 1.25}rem` }}
      >
        <div className="d-flex align-items-start gap-2 flex-grow-1">
          <span
            className="rounded-circle flex-shrink-0 mt-1"
            style={{
              width: '10px',
              height: '10px',
              backgroundColor:
                colorMap[getCategoryRoot(node)]
                || summaryChartColors[depth % summaryChartColors.length],
              opacity: Math.max(1 - (depth * 0.12), 0.5),
            }}
          />
          <div>
            <div className={depth === 0 ? 'fw-semibold' : ''}>
              {node.category_name}
            </div>
            {depth > 0 && (
              <div className="text-muted small sourcing-order-page__category-key-path">
                {node.category_full_name}
              </div>
            )}
            <div className="sourcing-order-page__category-bar mt-2">
              <div
                className="sourcing-order-page__category-bar-fill"
                style={{
                  width: `${parentQty > 0 ? Math.max((node.qty / parentQty) * 100, 4) : 0}%`,
                  backgroundColor:
                    colorMap[getCategoryRoot(node)]
                    || summaryChartColors[depth % summaryChartColors.length],
                  opacity: Math.max(1 - (depth * 0.12), 0.5),
                }}
              />
            </div>
          </div>
        </div>
        <span className="fw-semibold">{node.qty}</span>
      </div>
      {node.children && node.children.length > 0
        ? renderCategoryHierarchy(node.children, colorMap, node.qty, depth + 1)
        : null}
    </div>
  ));

  // const strippedTags = ['TV Parts', 'Internal Parts', 'Offer'];
  const {
    shipped,
    losers,
    financials,
    salesTrend,
    categories: categoryBreakdown = [],
  } = summaryData;
  const inductedPieces = Number(shipped?.inducted_qty) || 0;
  const shippedPieces = Number(shipped?.qty) || 0;
  const shippedProgress = inductedPieces > 0
    ? Math.min((shippedPieces / inductedPieces) * 100, 100)
    : 0;
  const progressRadius = 42;
  const progressCircumference = 2 * Math.PI * progressRadius;
  const progressOffset = progressCircumference
    - ((shippedProgress / 100) * progressCircumference);
  const revenueMinusCostClass = (Number(financials?.profit) || 0) < 0
    ? 'text-danger'
    : 'text-success';

  let shippedCardContent = (
    <div className="text-muted">
      {t('No shipped items for this lot')}
    </div>
  );

  if (summaryLoading.shipped) {
    shippedCardContent = renderSummaryCardLoader(t('Loading shipped summary...'));
  } else if (summaryErrors.shipped) {
    shippedCardContent = renderSummaryCardError();
  } else if (shipped && shipped.items && shipped.items.length > 0) {
    shippedCardContent = (
      <div
        className="px-2 flex-grow-1"
        style={{
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <Table
          size="sm"
          className="table table-sm table-striped mb-0"
        >
          <Thead>
            <Tr>
              <Th className="ps-2">
                {t('Part Number')}
              </Th>
              <Th className="text-end pe-2">
                {t('Inducted')}
              </Th>
              <Th className="text-end pe-2">
                {t('Shipped')}
              </Th>
              <Th className="text-end pe-2">
                {t('Substituted')}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {shipped.items.map((item) => (
              <Tr
                key={item.item_id}
                className={classnames('sourcing-order-page__summary-row', {
                  'table-success':
                    item.shipped_qty > 0
                    && (
                      item.inducted_qty === 0
                      || item.shipped_qty >= item.inducted_qty
                    ),
                })}
                onClick={() => inspectSummaryItem(item.item_id)}
                style={{ cursor: 'pointer' }}
              >
                <Td className="ps-2">
                  <span className="sourcing-order-page__summary-link">
                    {item.manufacturer}
                    {' '}
                    {item.part_number}
                  </span>
                </Td>
                <Td className="text-end pe-2">
                  {item.inducted_qty}
                </Td>
                <Td className="text-end pe-2">
                  {item.shipped_qty}
                </Td>
                <Td className="text-end pe-2">
                  {item.substituted_shipped_qty || ''}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
    );
  }

  let losersCardContent = (
    <>
      <div className="display-6 mb-2">--</div>
      <div className="text-muted">
        {t('This box appears once the order is more than 30 days old')}
      </div>
    </>
  );

  if (summaryLoading.losers) {
    losersCardContent = renderSummaryCardLoader(t('Loading losers summary...'));
  } else if (summaryErrors.losers) {
    losersCardContent = renderSummaryCardError();
  } else if (losers?.is_older_than_30_days) {
    losersCardContent = (
      <>
        <div className="display-6 mb-2">
          {losers.total_unsold_qty}
        </div>
        <div className="text-muted mb-2">
          {t('Unsold inducted quantity')}
        </div>
        {losers.items.length > 0 ? (
          <div
            className="px-2 flex-grow-1"
            style={{
              minHeight: 0,
              overflowY: 'auto',
            }}
          >
            <Table
              size="sm"
              className="table table-sm table-striped mb-0"
            >
              <Thead>
                <Tr>
                  <Th className="ps-2">
                    {t('Item')}
                  </Th>
                  <Th className="text-end pe-2">
                    {t('Qty')}
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {losers.items.map((item) => (
                  <Tr
                    key={item.item_id}
                    className="sourcing-order-page__summary-row"
                    onClick={() => inspectSummaryItem(item.item_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Td className="ps-2">
                      <span className="sourcing-order-page__summary-link">
                        {item.manufacturer}
                        {' '}
                        {item.part_number}
                      </span>
                    </Td>
                    <Td className="text-end pe-2">
                      {item.unsold_qty}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        ) : (
          <div className="text-muted">
            {t('No unsold inducted items')}
          </div>
        )}
      </>
    );
  }

  let piecesProgressContent = (
    <div className="d-flex flex-column flex-md-row align-items-center gap-4">
      <div
        className="position-relative d-flex align-items-center justify-content-center"
        style={{ width: '120px', height: '120px' }}
      >
        <svg width="120" height="120" viewBox="0 0 120 120">
          <circle
            cx="60"
            cy="60"
            r={progressRadius}
            fill="none"
            stroke="rgba(148, 163, 184, 0.22)"
            strokeWidth="10"
          />
          <circle
            cx="60"
            cy="60"
            r={progressRadius}
            fill="none"
            stroke="#3f9a90"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={progressCircumference}
            strokeDashoffset={progressOffset}
            transform="rotate(-90 60 60)"
          />
        </svg>
        <div className="position-absolute text-center">
          <div className="h4 mb-0">
            {`${Math.round(shippedProgress)}%`}
          </div>
          <div className="text-muted small">{t('Shipped')}</div>
        </div>
      </div>

      <div className="flex-grow-1">
        <Row className="g-3">
          <Col sm="4">
            <div className="text-muted small text-uppercase mb-1">
              {t('Total Inducted')}
            </div>
            <div className="h3 mb-0">{inductedPieces}</div>
          </Col>
          <Col sm="4">
            <div className="text-muted small text-uppercase mb-1">
              {t('Total Shipped')}
            </div>
            <div className="h3 mb-0">{shippedPieces}</div>
          </Col>
          <Col sm="4">
            <div className="text-muted small text-uppercase mb-1">
              {t('Pieces Remaining')}
            </div>
            <div className="h3 mb-0">
              {Math.max(inductedPieces - shippedPieces, 0)}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );

  if (summaryLoading.shipped) {
    piecesProgressContent = renderSummaryCardLoader(t('Loading pieces progress...'));
  } else if (summaryErrors.shipped) {
    piecesProgressContent = renderSummaryCardError();
  }

  let categoryBreakdownContent = null;
  let salesTrendContent = null;
  let popularityDistributionContent = null;
  let saleSiteDistributionContent = null;

  if (summaryLoading.categories) {
    categoryBreakdownContent = (
      <Card className="h-100">
        <CardBody className="d-flex">
          {renderSummaryCardLoader(t('Loading category mix...'))}
        </CardBody>
      </Card>
    );
  } else if (summaryErrors.categories) {
    categoryBreakdownContent = (
      <Card className="h-100">
        <CardBody>{renderSummaryCardError()}</CardBody>
      </Card>
    );
  } else {
    const activeTree = categoryBreakdown.find(
      (tree) => String(tree.tree_id) === activeCategoryTreeTab,
    ) || categoryBreakdown[0];

    categoryBreakdownContent = (
      <Card className="h-100">
        <CardBody>
          <Nav tabs className="sourcing-order-page__subtabs mb-3">
            {categoryBreakdown.map((tree) => (
              <NavItem key={tree.tree_id}>
                <NavLink
                  className={classnames(
                    'sourcing-order-page__subtab',
                    { active: String(tree.tree_id) === String(activeCategoryTreeTab) },
                  )}
                  onClick={() => setActiveCategoryTreeTab(String(tree.tree_id))}
                >
                  {tree.tree_name}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          {activeTree ? (() => {
            const rootNodes = activeTree.hierarchy || [];
            const totalQty = rootNodes.reduce(
              (sum, node) => sum + node.qty,
              0,
            );
            const colorMap = rootNodes.reduce((acc, node, index) => ({
              ...acc,
              [getCategoryRoot(node)]: summaryChartColors[index % summaryChartColors.length],
            }), {});

            return (
              <div>
                <div className="text-muted small text-uppercase mb-3">
                  {activeTree.tree_title || activeTree.tree_name}
                </div>
                <div
                  className="pe-2"
                  style={{ maxHeight: '320px', overflowY: 'auto' }}
                >
                  {rootNodes.length > 0 ? (
                    renderCategoryHierarchy(rootNodes, colorMap, totalQty)
                  ) : (
                    <div className="text-muted">
                      {t('No category data')}
                    </div>
                  )}
                </div>
              </div>
            );
          })() : (
            <div className="text-muted">
              {t('No category data')}
            </div>
          )}
        </CardBody>
      </Card>
    );
  }

  const salesTrendPoints = Array.isArray(salesTrend?.points)
    ? salesTrend.points
    : [];
  const weeklyTrendPoints = Array.isArray(salesTrend?.weekly_points)
    ? salesTrend.weekly_points
    : buildWeeklyTrendPoints(salesTrendPoints);
  const trendSeries = weeklyTrendPoints.map((point) => Number(point.sales_count) || 0);
  const trendLine = Array.isArray(salesTrend?.trend_line)
    ? salesTrend.trend_line
    : buildTrendLine(trendSeries);
  const revenueHalfLife = salesTrend?.metrics?.revenue_half_life || null;
  const costRecoveryMetric = salesTrend?.metrics?.cost_recovery || null;
  const inventoryHalfLife = salesTrend?.metrics?.inventory_half_life || null;

  if (summaryLoading.salesTrend) {
    salesTrendContent = (
      <Card className="h-100">
        <CardBody className="d-flex">
          {renderSummaryCardLoader(t('Loading sales trend...'))}
        </CardBody>
      </Card>
    );
  } else if (summaryErrors.salesTrend) {
    salesTrendContent = (
      <Card className="h-100">
        <CardBody>{renderSummaryCardError()}</CardBody>
      </Card>
    );
  } else {
    const salesMax = trendSeries.reduce((max, value) => (value > max ? value : max), 0);
    const revenueMax = weeklyTrendPoints.reduce(
      (max, point) => ((Number(point.revenue) || 0) > max ? Number(point.revenue) || 0 : max),
      0,
    );

    const renderMetric = (label, metric) => {
      const metricDate = formatMetricDate(metric, t);

      return (
        <div>
          <div className="text-muted small text-uppercase">
            {label}
          </div>
          <div className="h5 mb-0">
            {metric
              ? `${formatHalfLifeWeeks(metric.weeks, t)} (${t(metric.mode === 'actual' ? 'actual' : 'predicted')})`
              : t('Not available yet')}
          </div>
          {metricDate && (
            <div className="text-muted small mt-1">
              {metricDate}
            </div>
          )}
        </div>
      );
    };

    salesTrendContent = (
      <Card className="h-100">
        <CardBody>
          <div className="text-muted small text-uppercase mb-3">
            {t('Weekly Sales Trend Since Order Created')}
          </div>
          <div className="mb-3 d-grid" style={{ gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {renderMetric(t('Revenue Half-Life'), revenueHalfLife)}
            {renderMetric(t('Full Cost Recovery'), costRecoveryMetric)}
            {renderMetric(t('Inventory Half-Life'), inventoryHalfLife)}
          </div>
          {weeklyTrendPoints.length > 0 ? (
            <Chart
              type="line"
              height={280}
              series={[
                {
                  name: t('Sales'),
                  data: weeklyTrendPoints.map((point) => ({
                    x: point.date,
                    y: Number(point.sales_count) || 0,
                    revenue: Number(point.revenue) || 0,
                  })),
                },
                {
                  name: t('Trend'),
                  data: weeklyTrendPoints.map((point, index) => ({
                    x: point.date,
                    y: trendLine[index],
                  })),
                },
              ]}
              options={{
                chart: {
                  toolbar: { show: false },
                  zoom: { enabled: false },
                },
                colors: ['#3b82f6', '#94a3b8'],
                stroke: {
                  width: [3, 2],
                  curve: 'smooth',
                  dashArray: [0, 6],
                },
                markers: {
                  size: [3, 0],
                },
                dataLabels: {
                  enabled: false,
                },
                xaxis: {
                  type: 'datetime',
                  labels: {
                    datetimeUTC: false,
                    format: 'MMM dd',
                  },
                },
                yaxis: {
                  title: {
                    text: t('Sales'),
                  },
                  min: 0,
                  max: salesMax > 0 ? undefined : 1,
                  forceNiceScale: true,
                },
                tooltip: {
                  shared: true,
                  x: {
                    format: 'MMM dd, yyyy',
                  },
                  y: {
                    formatter: (value, opts) => {
                      if (opts.seriesIndex === 0) {
                        const point = weeklyTrendPoints[opts.dataPointIndex];
                        return `${value} sales | ${formatCurrency(point?.revenue)}`;
                      }

                      return `${Number(value).toFixed(2)} trend`;
                    },
                  },
                },
                legend: {
                  position: 'top',
                },
                grid: {
                  borderColor: 'rgba(148, 163, 184, 0.18)',
                },
                noData: {
                  text: t('No sales trend data'),
                },
              }}
            />
          ) : (
            <div className="text-muted">
              {t('No sales trend data')}
            </div>
          )}
          {revenueMax > 0 && (
            <div className="text-muted small mt-2">
              {t('Hover a week to see sales count and revenue')}
            </div>
          )}
        </CardBody>
      </Card>
    );
  }

  const siteDistributionRows = Array.isArray(salesTrend?.site_distribution)
    ? salesTrend.site_distribution
    : [];

  if (summaryLoading.salesTrend) {
    saleSiteDistributionContent = (
      <Card className="h-100">
        <CardBody className="d-flex">
          {renderSummaryCardLoader(t('Loading sale site distribution...'))}
        </CardBody>
      </Card>
    );
  } else if (summaryErrors.salesTrend) {
    saleSiteDistributionContent = (
      <Card className="h-100">
        <CardBody>{renderSummaryCardError()}</CardBody>
      </Card>
    );
  } else {
    const totalSiteSales = siteDistributionRows.reduce(
      (sum, row) => sum + (Number(row.sale_count) || 0),
      0,
    );
    const normalizedSiteDistributionRows = siteDistributionRows
      .filter((row) => (Number(row.sale_count) || 0) > 0)
      .map((row, index) => ({
        ...row,
        sale_count: Number(row.sale_count) || 0,
        share: totalSiteSales > 0 ? ((Number(row.sale_count) || 0) / totalSiteSales) : 0,
        color: siteDistributionColors[index % siteDistributionColors.length],
      }));

    saleSiteDistributionContent = (
      <Card className="h-100">
        <CardBody className="d-flex flex-column">
          <div className="text-muted small text-uppercase mb-3">
            {t('Sale Site Distribution')}
          </div>
          {normalizedSiteDistributionRows.length > 0 ? (
            <div className="d-flex flex-column justify-content-center flex-grow-1">
              <div className="d-flex flex-column flex-xl-row align-items-center align-items-xl-center gap-4">
                <div style={{ width: '300px', maxWidth: '100%' }}>
                  <Chart
                    type="donut"
                    height={300}
                    series={normalizedSiteDistributionRows.map((row) => row.sale_count)}
                    options={{
                      chart: {
                        toolbar: { show: false },
                      },
                      labels: normalizedSiteDistributionRows.map((row) => row.site),
                      colors: normalizedSiteDistributionRows.map((row) => row.color),
                      legend: {
                        show: false,
                      },
                      dataLabels: {
                        enabled: false,
                      },
                      stroke: {
                        width: 0,
                      },
                      plotOptions: {
                        pie: {
                          donut: {
                            size: '58%',
                            labels: {
                              show: false,
                            },
                          },
                        },
                      },
                      tooltip: {
                        y: {
                          formatter: (value, opts) => {
                            const row = normalizedSiteDistributionRows[opts.seriesIndex];
                            return `${value} ${t('sales')} | ${Number((row?.share || 0) * 100).toFixed(1)}%`;
                          },
                        },
                      },
                      noData: {
                        text: t('No sale site data'),
                      },
                    }}
                  />
                </div>

                <div className="flex-grow-1 w-100">
                  <div className="d-flex flex-column gap-2">
                    {normalizedSiteDistributionRows.map((row) => (
                      <div
                        key={row.site}
                        className="d-flex align-items-center justify-content-between gap-3"
                      >
                        <div className="d-flex align-items-center gap-2 min-w-0">
                          <span
                            style={{
                              width: '10px',
                              height: '10px',
                              minWidth: '10px',
                              borderRadius: '999px',
                              backgroundColor: row.color,
                              display: 'inline-block',
                            }}
                          />
                          <span className="text-truncate">
                            {row.site}
                          </span>
                        </div>
                        <div className="text-muted small text-nowrap">
                          {row.sale_count}
                          {' '}
                          {t('sales')}
                          {' '}
                          <span className="ms-2">
                            {`${Number(row.share * 100).toFixed(1)}%`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-muted d-flex flex-grow-1 align-items-center">
              {t('No sale site data')}
            </div>
          )}
        </CardBody>
      </Card>
    );
  }

  const popularityDistributionRows = Array.isArray(shipped?.distribution_items)
    ? shipped.distribution_items
    : [];

  if (summaryLoading.shipped) {
    popularityDistributionContent = (
      <Card className="h-100">
        <CardBody className="d-flex">
          {renderSummaryCardLoader(t('Loading popularity distribution...'))}
        </CardBody>
      </Card>
    );
  } else if (summaryErrors.shipped) {
    popularityDistributionContent = (
      <Card className="h-100">
        <CardBody>{renderSummaryCardError()}</CardBody>
      </Card>
    );
  } else {
    const popularityMaxQty = Number(shipped?.inducted_qty) || 0;
    const popularityDistributionPoints = buildPopularityDistributionPoints(
      popularityDistributionRows,
      popularityMaxQty,
    );
    const popularityMaxScore = popularityDistributionPoints.reduce(
      (max, point) => (point.y > max ? point.y : max),
      0,
    );
    const popularityChartRoof = Math.max(popularityMaxScore, 1);

    popularityDistributionContent = (
      <Card className="h-100">
        <CardBody>
          <div className="text-muted small text-uppercase mb-3">
            {t('Item Popularity Distribution')}
          </div>
          {popularityDistributionPoints.length > 0 ? (
            <>
              <Chart
                type="area"
                height={250}
                series={[
                  {
                    name: t('Popularity'),
                    data: popularityDistributionPoints,
                  },
                ]}
                options={{
                  chart: {
                    toolbar: { show: false },
                    zoom: { enabled: false },
                  },
                  colors: ['#10b981'],
                  stroke: {
                    width: 3,
                    curve: 'smooth',
                  },
                  fill: {
                    type: 'gradient',
                    gradient: {
                      shadeIntensity: 1,
                      opacityFrom: 0.52,
                      opacityTo: 0.08,
                      stops: [0, 100],
                    },
                  },
                  dataLabels: {
                    enabled: false,
                  },
                  markers: {
                    size: 0,
                  },
                  xaxis: {
                    type: 'numeric',
                    min: 0,
                    max: 100,
                    title: {
                      text: t('Distribution'),
                    },
                    labels: {
                      show: false,
                    },
                    axisTicks: {
                      show: false,
                    },
                    axisBorder: {
                      show: false,
                    },
                  },
                  yaxis: {
                    min: 0,
                    max: popularityChartRoof,
                    title: {
                      text: t('Popularity'),
                    },
                    labels: {
                      show: false,
                    },
                  },
                  tooltip: {
                    x: {
                      formatter: (value) => `${Number(value).toFixed(0)}% ${t('of inducted parts')}`,
                    },
                    y: {
                      formatter: (value, opts) => {
                        const point = popularityDistributionPoints[opts.dataPointIndex];
                        return `${Number(value).toFixed(2)} ${t('popularity score')} | ${Math.round(point?.range_start_qty || 0)}-${Math.round(point?.range_end_qty || 0)} / ${popularityMaxQty} ${t('parts')}`;
                      },
                    },
                  },
                  legend: {
                    show: false,
                  },
                  grid: {
                    borderColor: 'rgba(148, 163, 184, 0.18)',
                  },
                  noData: {
                    text: t('No popularity data'),
                  },
                }}
              />
              <div className="d-flex align-items-start gap-2 text-muted small mt-2">
                <i className="bx bx-info-circle fs-5 lh-1" aria-hidden="true" />
                <span>
                  {t('Shows this order from its most popular inducted parts on the left to its least popular on the right.')}
                </span>
              </div>
            </>
          ) : (
            <div className="text-muted">
              {t('No popularity data')}
            </div>
          )}
        </CardBody>
      </Card>
    );
  }

  let revenueContent = (
    <div>
      <div className="h3 mb-0">
        {formatCurrency(financials?.revenue)}
      </div>
      <div className="sourcing-order-page__summary-subvalue mt-2">
        {t('Potential')}
        {': '}
        {formatCurrency(financials?.potential_revenue)}
      </div>
    </div>
  );

  let profitContent = (
    <div className={`h3 mb-0 ${revenueMinusCostClass}`}>
      {formatCurrency(financials?.profit)}
    </div>
  );

  let remainingToCostContent = (
    <div className="h3 mb-0">
      {formatCurrency(financials?.remaining_to_cost)}
    </div>
  );

  if (summaryLoading.financials) {
    revenueContent = renderSummaryCardLoader(t('Loading revenue...'));
    profitContent = renderSummaryCardLoader(t('Loading margin...'));
    remainingToCostContent = renderSummaryCardLoader(t('Loading cost target...'));
  } else if (summaryErrors.financials) {
    revenueContent = renderSummaryCardError();
    profitContent = renderSummaryCardError();
    remainingToCostContent = renderSummaryCardError();
  }

  return (
    <div className="page-content sourcing-order-page">
      <div className="container-fluid">
        {isInitialLoading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 my-5">
            <Spinner
              color="primary"
              style={{ width: '3rem', height: '3rem' }}
            />
            <div className="mt-3 text-muted">
              {t('Loading order details...')}
            </div>
          </div>
        ) : (
          <div>
            <Row>
              <Col md="3">
                {part !== null && (
                  <Card className="sourcing-order-page__card">
                    <CardHeader className="sourcing-order-page__card-header bg-transparent border-bottom">
                      {part.manufacturer}
                      {' '}
                      {part.part_number}
                    </CardHeader>
                    <CardBody className="sourcing-order-page__card-body">
                      <Inspector item={part} device={entity.device} />
                    </CardBody>
                  </Card>
                )}

                <Card className="sourcing-order-page__card sourcing-order-page__summary-card">
                  <CardHeader className="sourcing-order-page__card-header bg-transparent border-bottom">
                    {entity !== null ? entity.reference : '--'}
                  </CardHeader>
                  <CardBody className="sourcing-order-page__card-body">
                    <div className="table-responsive sourcing-order-page__summary-table-wrap">
                      <Table
                        size="sm"
                        className="table table-bordered table-striped sourcing-order-page__summary-table"
                      >
                        <Tbody>
                          {entity !== null && (
                            <>
                              <Tr>
                                <Th>{t('Reference')}</Th>
                                <Td>{entity.reference}</Td>
                              </Tr>
                              {entity.supplier !== undefined && (
                                <>
                                  <Tr>
                                    <Th>{t('Supplier')}</Th>
                                    <Td>
                                      <Link
                                        to={`/sourcing/supplier/${entity.supplier.id}`}
                                      >
                                        {entity.supplier.name}
                                      </Link>
                                    </Td>
                                  </Tr>
                                  <Tr>
                                    <Th>{t('Program')}</Th>
                                    <Td>
                                      {entity.supplier_program === null
                                        ? '--'
                                        : entity.supplier_program}
                                    </Td>
                                  </Tr>
                                </>
                              )}
                              <Tr>
                                <Th>{t('Cost')}</Th>
                                <Td>{formatCurrency(entity.cost)}</Td>
                              </Tr>
                              <Tr>
                                <Th>{t('Potential Revenue')}</Th>
                                <Td>{formatCurrency(financials?.potential_revenue)}</Td>
                              </Tr>
                              <Tr>
                                <Th>{t('Lot')}</Th>
                                <Td>{entity.lot}</Td>
                              </Tr>
                              {entity.notes !== null && entity.notes !== undefined && String(entity.notes).trim() !== '' && (
                                <Tr>
                                  <Th>{t('Notes')}</Th>
                                  <Td style={{ whiteSpace: 'pre-wrap' }}>{entity.notes}</Td>
                                </Tr>
                              )}
                            </>
                          )}
                        </Tbody>
                      </Table>
                    </div>

                    <div className="mt-2">
                      <div className="hstack gap-2">
                        <Button
                          color="warning"
                          className="sourcing-order-page__summary-action sourcing-order-page__summary-action--edit"
                          onClick={() => setEditModalOpen(true)}
                        >
                          <span className="mdi mdi-pencil" />
                        </Button>
                        <Button
                          color="primary"
                          className="sourcing-order-page__summary-action sourcing-order-page__summary-action--cost btn-soft-primary"
                          onClick={calculateCost}
                        >
                          <span className="mdi mdi-cash" />
                          {' '}
                          {t('Cost')}
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col md="9">
                <Nav tabs className="sourcing-order-page__tabs">
                  <NavItem>
                    <NavLink
                      className={classnames('sourcing-order-page__tab', {
                        active: activeTab === '0',
                        'sourcing-order-page__tab--active': activeTab === '0',
                      })}
                      onClick={() => {
                        toggle('0');
                      }}
                    >
                      {t('Induction')}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames('sourcing-order-page__tab', {
                        active: activeTab === '1',
                        'sourcing-order-page__tab--active': activeTab === '1',
                      })}
                      onClick={() => {
                        toggle('1');
                      }}
                    >
                      {t('From Manifest')}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames('sourcing-order-page__tab', {
                        active: activeTab === '2',
                        'sourcing-order-page__tab--active': activeTab === '2',
                      })}
                      onClick={() => {
                        toggle('2');
                      }}
                    >
                      {t('History')}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames('sourcing-order-page__tab', {
                        active: activeTab === '3',
                        'sourcing-order-page__tab--active': activeTab === '3',
                      })}
                      onClick={() => {
                        toggle('3');
                      }}
                    >
                      {t('Summary')}
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="0">
                    <div className="sourcing-order-page__panel p-3">
                      <Row>
                        <Col xl="4">
                          <label
                            htmlFor="induction_search"
                            className="form-label"
                          >
                            {t('Search for Item')}
                          </label>
                          <InputGroup
                            className="mb-3"
                          >
                            <input
                              type="text"
                              id="induction_search"
                              className="form-control"
                              value={inductionSearchValue}
                              ref={searchFieldRef}
                              onChange={(e) => setInductionSearchValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.keyCode === 13) {
                                  setSearchTerm(inductionSearchValue.trim());
                                }
                              }}
                            />
                            <Button
                              color="primary"
                              className="btn-soft-primary"
                              onClick={() => {
                                if (
                                  inductionSearchValue.trim() === searchTerm
                                ) {
                                  setSignal(signal + 1);
                                } else {
                                  setSearchTerm(inductionSearchValue.trim());
                                }
                              }}
                            >
                              <span
                                className={
                                  inductionSearchValue.trim() === searchTerm
                                    && searchTerm !== ''
                                    ? 'mdi mdi-refresh'
                                    : 'mdi mdi-magnify'
                                }
                              />
                            </Button>
                          </InputGroup>
                        </Col>

                        <Col xl="4">
                          <label htmlFor="qty" className="form-label">
                            {t('Qty to Induct/Print')}
                          </label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            max="100"
                            ref={qtyRef}
                            id="qty"
                            className="form-control"
                            onChange={(e) => {
                              setInductQty(parseInt(e.target.value, 10));
                            }}
                          />
                        </Col>
                        <Col xl="4">
                          <div className="form-label">
                            {t('Manifest Upload')}
                          </div>
                          <div className="d-flex gap-2">
                            <input
                              ref={manifestUploadRef}
                              type="file"
                              accept=".csv"
                              className="d-none"
                              onChange={uploadManifest}
                            />
                            <Button
                              color="primary"
                              className="btn-soft-primary"
                              onClick={() => manifestUploadRef.current?.click()}
                            >
                              <span className="mdi mdi-upload" />
                              {' '}
                              {t('Upload Manifest')}
                            </Button>
                          </div>
                          <small className="text-muted d-block mt-2">
                            {t('Accepts Part Number, Qty or Supplier Sku, Qty and converts rows into inductions.')}
                          </small>
                        </Col>
                      </Row>

                      {searchTerm === '' && (
                        <em className="text-muted">
                          {t('Awaiting a search query...')}
                        </em>
                      )}

                      <div className="mt-3">
                        <Nav tabs className="sourcing-order-page__subtabs">
                          <NavItem>
                            <NavLink
                              className={classnames(
                                'sourcing-order-page__subtab',
                                {
                                  active: subActiveTab === '1',
                                },
                              )}
                              onClick={() => {
                                toggleSub('1');
                              }}
                            >
                              {t('Items')}
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              className={classnames(
                                'sourcing-order-page__subtab',
                                {
                                  active: subActiveTab === '2',
                                },
                              )}
                              onClick={() => {
                                toggleSub('2');
                              }}
                            >
                              {t('Kits')}
                            </NavLink>
                          </NavItem>
                        </Nav>

                        <TabContent
                          activeTab={subActiveTab}
                          className="text-muted sourcing-order-page__subpanel"
                        >
                          <TabPane tabId="1">
                            <div className="pt-3">
                              <BaseGrid
                                url="/api/sourcing/orders/induction-search"
                                search={searchTerm}
                                shouldUpdate={signal}
                                onDataLoaded={loadAreaStockForItems}
                                className="table table-bordered table-striped table-sm"
                                config={{
                                  rowClass: (x) => {
                                    if (
                                      x.scrap !== null
                                      && x.scrap > -1
                                      && x.scrap < x.qty
                                    ) {
                                      return 'table-danger';
                                    }

                                    return '';
                                  },
                                  columns: {
                                    thumb: {
                                      label: '',
                                      image: {
                                        src: (item) => `/api/items/thumbnail/${item.id}`,
                                        class: 'rounded',
                                        style: {
                                          maxHeight: '3rem',
                                          maxWidth: '3rem',
                                        },
                                      },
                                      translate: false,
                                    },
                                    id: {
                                      label: props.t('ID'),
                                      sort: true,
                                      link: {
                                        click: (item) => {
                                          setPart(item);
                                        },
                                      },
                                    },
                                    part_number: {
                                      label: props.t('Part Number'),
                                      sort: true,
                                      link: {
                                        click: (item) => {
                                          setPart(item);
                                        },
                                      },
                                    },
                                    alert: {
                                      label: props.t('Alert'),
                                      class: 'sourcing-order-page__alert-cell',
                                      format: (value) => formatSourcingAlert(value ? props.t(value) : ''),
                                    },
                                    qty: {
                                      label: props.t('Qty / Scrap'),
                                      sort: true,
                                      format: (f, x) => {
                                        let qty = f;
                                        if (
                                          f !== null
                                          && x.scrap !== null
                                          && x.scrap > -1
                                        ) {
                                          qty = `${f.toString()} / ${x.scrap.toString()}`;
                                        }

                                        return qty;
                                      },
                                    },
                                    area_stock: {
                                      label: props.t('SJ Owned'),
                                      sort: false,
                                      format: (f, x) => formatAreaStock(
                                        itemAreaStock[x.id]
                                        || areaStockCache[x.id],
                                        loadingAreaStock[x.id],
                                      ),
                                    },
                                    boardNumbers: {
                                      label: props.t('Board Numbers'),
                                      html: true,
                                      format: (f) => {
                                        const bns = f
                                          .map((b) => (b.label === 'Visible Number'
                                            ? b.value
                                            : ''))
                                          .filter((x) => x !== '')
                                          .join('<br />');

                                        return bns;
                                      },
                                    },
                                    searchable: {
                                      label: props.t('Search'),
                                      html: true,
                                      format: (f, v) => {
                                        const bns = v.boardNumbers
                                          .map((b) => (b.label === 'Search' ? b.value : ''))
                                          .filter((x) => x !== '')
                                          .join('<br />');

                                        return bns;
                                      },
                                    },
                                    devices: {
                                      label: props.t('Knwn mdls'),
                                      html: true,
                                      style: {
                                        fontSize: '11px',
                                      },
                                      format: (x) => `<div style="max-height: 100px; overflow-y: auto; min-width: 150px;">${x
                                        .map((r) => r.model)
                                        .sort()
                                        .join('<br />')}</div>`,
                                    },
                                    mnfctrpn: {
                                      label: props.t('Mfr. Prt No'),
                                      html: true,
                                      format: (f, x) => {
                                        const bns = x.boardNumbers
                                          .map((b) => (b.label
                                            === 'Manufacturer Part Number'
                                            ? b.value
                                            : ''))
                                          .map((c) => c.replace(x.manufacturer, '').trim())
                                          .filter(
                                            (y) => y !== '' && y !== x.part_number,
                                          )
                                          .join('<br />');

                                        return bns;
                                      },
                                    },
                                    // tags: {
                                    //   label: 'Tags',
                                    //   format: (x) => (
                                    //  x.map((r) => (
                                    //    !strippedTags.includes(r.label) ? r.label : ''
                                    //  ))
                                    //  .filter(() => x !== '')),
                                    //   sort: true,
                                    //   badges: {
                                    //     class: 'bg-info bg-soft text-dark p-1',
                                    //   },
                                    // },
                                    actions: {
                                      label: props.t('Actions'),
                                      actions: [
                                        {
                                          key: 'print',
                                          icon: 'bx bx-printer',
                                          class: 'btn btn-sm btn-soft-dark',
                                          alt: props.t('Induct'),
                                          click: induct,
                                        },
                                      ],
                                    },
                                  },
                                  pagination: {
                                    sort: 'id',
                                    direction: 'desc',
                                    perPage: 25,
                                  },
                                }}
                              />
                            </div>
                          </TabPane>
                          <TabPane tabId="2">
                            <div className="pt-3">
                              <BaseGrid
                                url="/api/sourcing/orders/kit-search"
                                search={searchTerm}
                                className="table table-bordered table-striped table-sm"
                                config={{
                                  columns: {
                                    thumb: {
                                      label: '',
                                      image: {
                                        src: (lst) => `/api/listings/thumbnail/${lst.id}`,
                                        class: 'rounded',
                                        style: {
                                          maxHeight: '3rem',
                                          maxWidth: '3rem',
                                        },
                                      },
                                      translate: false,
                                    },
                                    id: {
                                      label: props.t('ID'),
                                      sort: true,
                                    },
                                    sku: {
                                      label: props.t('Sku'),
                                      sort: true,
                                    },
                                    title: {
                                      label: props.t('Title'),
                                      sort: true,
                                      style: { maxWidth: '200px' },
                                      html: true,
                                      format: (f, x) => `<div>${f}</div><div class="text-danger" style="font-size: 9px;">${x.important}</div>`,
                                    },
                                    boardNumbers: {
                                      label: props.t('Mnfr PN'),
                                      html: true,
                                      format: (f, x) => {
                                        const bns = x.items
                                          .map((itm) => itm.boardNumbers)
                                          .flat()
                                          .filter((y) => (y.label
                                            ? [
                                              'Manufacturer Part Number',
                                            ].includes(y.label.trim())
                                            : false))
                                          .map((w) => w.value)
                                          .join('<br />');

                                        const atts = x.attributes
                                          && x.attributes
                                            .filter((y) => [
                                              'Manufacturer Part Number',
                                            ].includes(y.label))
                                            .map((w) => w.code)
                                            .join('<br />');

                                        return `${bns}<br />${atts}`;
                                      },
                                    },
                                    devices: {
                                      label: props.t('Knwn mdls'),
                                      html: true,
                                      style: {
                                        fontSize: '11px',
                                      },
                                      format: (x) => `<div style="max-height: 100px; overflow-y: auto; min-width: 150px;">${x
                                        .map((r) => r.model)
                                        .sort()
                                        .join('<br />')}</div>`,
                                    },
                                    last_qty: {
                                      label: props.t('Qty'),
                                    },
                                    items: {
                                      label: props.t('Parts'),
                                      translate: false,
                                      format: (i) => i.map((x) => (
                                        <div>
                                          <Button
                                            className="p-0 m-0"
                                            style={{
                                              fontSize: '11px',
                                              color: '#0B0',
                                            }}
                                            color="link"
                                            onClick={() => {
                                              setPart(x);
                                            }}
                                          >
                                            {x.part_number}
                                            {' '}
                                            {`(x${x.stock.reduce(
                                              (a, c) => a + c.qty,
                                              0,
                                            )})`}
                                          </Button>
                                        </div>
                                      )), // XSS risk
                                    },
                                    actions: {
                                      label: props.t('Actions'),
                                      actions: [
                                        {
                                          key: 'print',
                                          icon: 'bx bx-printer',
                                          class: 'btn btn-sm btn-soft-dark',
                                          alt: props.t('Process'),
                                          click: processKit,
                                        },
                                      ],
                                    },
                                  },
                                  pagination: {
                                    sort: 'id',
                                    direction: 'desc',
                                    perPage: 25,
                                  },
                                }}
                              />
                            </div>
                          </TabPane>
                        </TabContent>
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tabId="1" className="sourcing-order-page__panel p-3">
                    {entity !== null && (
                      <Formik
                        enableReinitialize
                        initialValues={{
                          manifest:
                            entity.items !== undefined
                            && entity.items.reduce((acc, item) => {
                              acc[`${item.id}`] = { qty: item.qty };
                              return acc;
                            }, {}),
                        }}
                        validationSchema={createValidationSchema(entity.items)}
                        onSubmit={async (values) => {
                          await post(
                            `/api/sourcing/orders/print/${id}`,
                            values,
                          );
                        }}
                      >
                        {({ values }) => (
                          <Form id="SupplierOrderForm" autoComplete="off">
                            <BaseGrid
                              url={`/api/sourcing/orders/${id}/items`}
                              hasSearch
                              shouldUpdate={signal}
                              className="table table-bordered table-striped table-sm"
                              filters={`oi.sourcing_orders_id=${id}`}
                              config={{
                                columns: {
                                  thumb: {
                                    label: '',
                                    image: {
                                      condition: (x) => x.supplier_item
                                        && x.supplier_item.linked_item !== null,
                                      src: (item) => {
                                        if (
                                          item.supplier_item
                                          && item.supplier_item.linked_item
                                          !== null
                                        ) {
                                          return `/api/items/thumbnail/${item.supplier_item.linked_item.id}`;
                                        }

                                        return '/api/items/thumbnail/0';
                                      },
                                      class: 'rounded',
                                      style: {
                                        maxHeight: '3rem',
                                        maxWidth: '5rem',
                                      },
                                    },
                                    translate: false,
                                  },
                                  supplier_id: {
                                    label: t('Supplier Sku'),
                                    format: (y, x) => (x.supplier_item
                                      ? x.supplier_item.supplier_sku
                                      : '--'),
                                    sort: true,
                                  },
                                  linked_item: {
                                    label: t('Item'),
                                    format: (y, x) => {
                                      if (
                                        x.supplier_item
                                        && x.supplier_item.linked_item !== null
                                      ) {
                                        return `${x.supplier_item.linked_item.manufacturer} ${x.supplier_item.linked_item.part_number}`;
                                      }

                                      return '--';
                                    },
                                    link: {
                                      condition: (x) => x.supplier_item
                                        && x.supplier_item.linked_item !== null,
                                      func: (x) => `/item/${x.supplier_item.linked_item.id}`,
                                    },
                                    sort: true,
                                  },
                                  price: {
                                    label: t('Price'),
                                    sort: true,
                                    format: (x) => new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    }).format(x),
                                  },
                                  qty: {
                                    label: t('Qty'),
                                    sort: true,
                                  },
                                  print: {
                                    label: t('Print'),
                                    field: {
                                      type: 'number',
                                      name: (item) => `manifest.${item.id}.qty`,
                                    },
                                  },
                                  actions: {
                                    label: props.t('Actions'),
                                    actions: [
                                      {
                                        key: 'print',
                                        icon: 'bx bx-printer',
                                        class: 'btn btn-sm btn-soft-dark',
                                        alt: props.t('Print'),
                                        click: (x) => {
                                          print(
                                            x.id,
                                            values.manifest[x.id].qty,
                                          );
                                        },
                                      },
                                    ],
                                  },
                                },
                                pagination: {
                                  sort: 'id',
                                  direction: 'desc',
                                  perPage: 10,
                                },
                              }}
                            />
                            <Button
                              color="primary"
                              type="submit"
                              className="btn-soft-primary mt-2"
                            >
                              <span className="mdi mdi-printer" />
                              {' '}
                              {t('Print All')}
                            </Button>
                          </Form>
                        )}
                      </Formik>
                    )}
                  </TabPane>
                  <TabPane tabId="2" className="sourcing-order-page__panel p-3">
                    {entity !== null && (
                      <>
                        <div className="mb-3">
                          <Button
                            color="primary"
                            className="btn-soft-primary"
                            onClick={downloadManifest}
                          >
                            <span className="mdi mdi-download" />
                            {' '}
                            {t('Download Manifest CSV')}
                          </Button>
                        </div>
                        <BaseGrid
                          url={`/api/sourcing/orders/${entity.id}/inductions`}
                          shouldUpdate={signal}
                          className="table table-bordered table-striped table-sm"
                          filters={`oi.sourcing_orders_id=${id}`}
                          hasSearch
                          config={{
                            columns: {
                              thumb: {
                                label: '',
                                image: {
                                  src: (item) => `/api/items/thumbnail/${item.item_id}`,
                                  class: 'rounded',
                                  style: { maxHeight: '3rem', maxWidth: '3rem' },
                                },
                                translate: false,
                              },
                              id: {
                                label: props.t('ID'),
                                sort: true,
                                link: {
                                  click: (item) => {
                                    setPart(item.item);
                                  },
                                },
                              },
                              manufacturer: {
                                label: props.t('Manufacturer'),
                                sort: true,
                                format: (f, x) => x.item.manufacturer,
                              },
                              part_number: {
                                label: props.t('Part Number'),
                                sort: true,
                                link: {
                                  click: (item) => {
                                    setPart(item.item);
                                  },
                                },
                                format: (f, x) => x.item.part_number,
                              },
                              condition: {
                                label: props.t('Condition'),
                                sort: true,
                                format: (f, x) => x.item.condition,
                              },
                              inducted_by: {
                                label: props.t('Inducted By'),
                              },
                              inducted_at: {
                                label: props.t('Inducted At'),
                                date: {
                                  format: (date) => moment(date).fromNow(),
                                  timezone: process.env.LOCAL_TZ,
                                },
                              },
                              qty: {
                                label: props.t('Qty'),
                              },
                              actions: {
                                label: props.t('Actions'),
                                actions: [
                                  {
                                    key: 'reprint',
                                    icon: 'bx bx-printer',
                                    class: 'btn btn-sm btn-soft-dark',
                                    alt: props.t('Re-Print'),
                                    click: (itm) => {
                                      reprintItem(itm.item, itm.qty);
                                    },
                                  },
                                  {
                                    key: 'reprint1',
                                    icon: 'bx bx-printer',
                                    class: 'btn btn-sm btn-soft-secondary',
                                    alt: props.t('Re-Print 1'),
                                    click: (itm) => {
                                      reprintItem(itm.item, 1);
                                    },
                                  },
                                  {
                                    key: 'remove',
                                    icon: 'mdi mdi-close-thick',
                                    class: 'btn btn-sm btn-soft-danger',
                                    alt: props.t('Remove Record'),
                                    click: (itm) => {
                                      removeRecord(itm.id);
                                    },
                                  },
                                ],
                              },
                            },
                            pagination: {
                              sort: 'id',
                              direction: 'desc',
                              perPage: 10,
                            },
                          }}
                        />
                      </>
                    )}
                  </TabPane>

                  <TabPane tabId="3">
                    <div className="sourcing-order-page__panel p-3">
                      <Card className="mb-3 border-0 shadow-sm">
                        <CardBody className="p-3 p-md-4">
                          <Row className="align-items-center g-3">
                            <Col lg="4">
                              <div className="text-muted small text-uppercase mb-1">
                                {t('Quick Search')}
                              </div>
                              <div className="h4 mb-0">
                                {t('Jump Into Induction')}
                              </div>
                            </Col>
                            <Col lg="8">
                              <form
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  submitInductionSearch(inductionSearchValue);
                                }}
                              >
                                <InputGroup size="lg">
                                  <input
                                    type="text"
                                    className="form-control border-end-0"
                                    placeholder={t(
                                      'Search by part number, board number, or sku',
                                    )}
                                    ref={summarySearchRef}
                                    value={inductionSearchValue}
                                    onChange={(e) => setInductionSearchValue(e.target.value)}
                                  />
                                  <Button
                                    color="primary"
                                    type="submit"
                                    className="px-4"
                                  >
                                    <span className="mdi mdi-magnify me-1" />
                                    {t('Search')}
                                  </Button>
                                </InputGroup>
                              </form>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>

                      <Row className="g-3 align-items-stretch">
                        <Col md="6">
                          <Card
                            className="h-100"
                            style={{ maxHeight: '350px' }}
                          >
                            <CardBody className="d-flex flex-column h-100">
                              <div className="text-muted small text-uppercase mb-2">
                                {t('Shipped')}
                              </div>
                              {shippedCardContent}
                            </CardBody>
                          </Card>
                        </Col>

                        <Col md="6">
                          <Card
                            className="h-100"
                            style={{ maxHeight: '350px' }}
                          >
                            <CardBody className="d-flex flex-column h-100">
                              <div className="text-muted small text-uppercase mb-2">
                                {t('Losers')}
                              </div>
                              {losersCardContent}
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>

                      <Row className="g-3 mt-1">
                        <Col md="12">
                          <Card className="h-100">
                            <CardBody>
                              <div className="text-muted small text-uppercase mb-3">
                                {t('Pieces Progress')}
                              </div>
                              {piecesProgressContent}
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>

                      <Row className="g-3 mt-1">
                        <Col md="4">
                          <Card className="h-100">
                            <CardBody>
                              <div className="text-muted small text-uppercase mb-2">
                                {t('Revenue From Order')}
                              </div>
                              {revenueContent}
                            </CardBody>
                          </Card>
                        </Col>
                        <Col md="4">
                          <Card className="h-100">
                            <CardBody>
                              <div className="text-muted small text-uppercase mb-2">
                                {t('Revenue Minus Cost')}
                              </div>
                              {profitContent}
                            </CardBody>
                          </Card>
                        </Col>
                        <Col md="4">
                          <Card className="h-100">
                            <CardBody>
                              <div className="text-muted small text-uppercase mb-2">
                                {t('Needed To Meet Cost')}
                              </div>
                              {remainingToCostContent}
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>

                      <Row className="g-3 mt-1">
                        <Col md="12">
                          {salesTrendContent}
                        </Col>
                      </Row>

                      <Row className="g-3 mt-1">
                        <Col md="6" lg="4">
                          {popularityDistributionContent}
                        </Col>
                        <Col md="6" lg="8">
                          {saleSiteDistributionContent}
                        </Col>
                      </Row>

                      <Row className="g-3 mt-1">
                        <Col md="12">
                          {categoryBreakdownContent}
                        </Col>
                      </Row>
                    </div>
                  </TabPane>
                </TabContent>
              </Col>
            </Row>

            <OrderModal
              shown={editModalOpen}
              item={entity}
              onSuccess={() => {
                setEditModalOpen(false);
                setSignal(signal + 1);
              }}
              onCancel={() => setEditModalOpen(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

Order.defaultProps = {
  t: (x) => x,
};

Order.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({}), {})(withTranslation()(Order));
